import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { buildBananaGirl, buildBananaBunch } from '../entities/bananaFolk.js';
import { createAmbient } from '../entities/ambient.js';
import { WeatherSystem, getWeatherState, weatherName, weatherIcon } from '../core/weather.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, spendCrumbs, setFlag, getFlag } from '../core/save.js';
import { restoreEnergy } from '../core/actions.js';
import { questsForGiver, acceptQuest, turnIn, isReady, objectiveProgress, QUESTS, flagObjective } from '../core/quests.js';

// Banana Village — a bright cluster of peel-shaped homes east of Toast Town.
// Run by Chiquita the banana smoothie maker, with cameos from the Banana Bunch.

export function createBananaVillageScene(){
  return () => new BananaVillageScene();
}

const PEEL = 0xF2D23B;
const PEEL_DARK = 0xD4B52F;
const TIP = 0x6E4526;

class BananaVillageScene {
  constructor(){
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xC9E8A0);
    this.scene.fog = new THREE.Fog(0xC9E8A0, 28, 64);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 120);
    this.camDist = 18;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildGround();
    this._buildHouses();
    this._buildProps();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 8);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.chiquita = this._makeChiquita();
    this.scene.add(this.chiquita.root);

    this.bunch = this._makeBunch();
    this.scene.add(this.bunch.root);

    this.ambient = createAmbient({ center: new THREE.Vector3(0, 0, 0), radius: 18, butterflies: 4, birds: 2 });
    this.scene.add(this.ambient.group);

    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, 0), radius: 26 });
    this._applyWeather();

    this.hotspots = [
      { pos: this.chiquita.root.position.clone(), r: 2.5, label: '🗣 talk to Chiquita', action: () => this._talkToChiquita() },
      { pos: this.bunch.root.position.clone(), r: 2.4, label: '🗣 talk to the Banana Bunch', action: () => this._talkToBunch() },
      { pos: new THREE.Vector3(0, 0, 8.5), r: 2.4, label: '← back to Toast Town', action: () => this.manager.goTo('town', { from: 'bananaVillage' }) }
    ];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.72));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.9);
    sun.position.set(8, 16, 8); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 28;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildGround(){
    const grass = new THREE.Mesh(new THREE.CircleGeometry(32, 56), new THREE.MeshLambertMaterial({ color: 0xA8D866 }));
    grass.rotation.x = -Math.PI/2; grass.receiveShadow = true; this.scene.add(grass);

    const path = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 12), new THREE.MeshLambertMaterial({ color: 0xC9A876 }));
    path.rotation.x = -Math.PI/2; path.position.set(0, 0.01, 10); this.scene.add(path);
  }

  _buildHouses(){
    const positions = [
      { x: -6, z: -2, s: 1.0 }, { x: 6, z: -3, s: 1.1 }, { x: -4, z: -8, s: 0.9 }, { x: 5, z: -9, s: 0.95 }
    ];
    positions.forEach(p => this._peelHouse(p.x, p.z, p.s));
  }

  _peelHouse(x, z, scale){
    const g = new THREE.Group(); g.position.set(x, 0, z); g.scale.set(scale, scale, scale); this.scene.add(g);
    // curved body: a cylinder leaning back
    const body = cyl(0.55, 0.45, 1.6, PEEL, 0, 0.8, 0, g, 14);
    body.rotation.z = -0.12;
    // peeled back skin flaps
    const flapL = box(0.5, 1.2, 0.08, PEEL_DARK, -0.45, 0.6, -0.55, g);
    flapL.rotation.set(-0.25, 0.25, 0.2);
    const flapR = box(0.5, 1.2, 0.08, PEEL_DARK, 0.45, 0.6, -0.55, g);
    flapR.rotation.set(-0.25, -0.25, -0.2);
    // door
    box(0.35, 0.7, 0.08, 0x5A3A2A, 0, 0.35, 0.42, g);
    // window
    box(0.28, 0.28, 0.06, 0xB8E8F5, 0.22, 0.9, 0.38, g);
    // stem and tip
    cyl(0.06, 0.06, 0.18, TIP, -0.12, 1.7, 0, g, 6).rotation.z = -0.12;
    cyl(0.05, 0.05, 0.12, 0x4A3A2A, 0.12, 0.08, 0, g, 6).rotation.z = -0.12;
  }

  _buildProps(){
    // smoothie stand
    const stand = new THREE.Group(); stand.position.set(3, 0, 3); this.scene.add(stand);
    box(2.0, 0.9, 1.0, 0xE23B4E, 0, 0.45, 0, stand);
    cyl(1.1, 1.2, 0.1, 0xF2D23B, 0, 1.0, 0, stand, 16);
    const awning = cyl(1.25, 1.25, 0.12, 0xFFFFFF, 0, 1.06, 0, stand, 16);
    // stripe texture-ish via radial slices is hard; just use white with a stripe mesh
    box(2.2, 0.12, 0.08, 0xFFFFFF, 0, 1.06, 0.6, stand);
    box(2.2, 0.12, 0.08, 0xE23B4E, 0, 1.06, 0.5, stand);
    const sign = textSprite('🍌 Smoothie Stand', 22, '#6B4A2F', 3.6, 0.9);
    sign.position.set(3, 2.4, 3); this.scene.add(sign);

    // banana trees (herbaceous)
    for (let i = 0; i < 6; i++){
      const a = (i / 6) * Math.PI * 2;
      const r = 11 + Math.random() * 4;
      this._bananaTree(Math.cos(a)*r, Math.sin(a)*r);
    }
  }

  _bananaTree(x, z){
    const g = new THREE.Group(); g.position.set(x, 0, z); this.scene.add(g);
    cyl(0.14, 0.18, 2.0, 0x4A7A3A, 0, 1.0, 0, g, 8);
    for (let i = 0; i < 5; i++){
      const leaf = new THREE.Group();
      leaf.position.set(0, 1.9, 0);
      leaf.rotation.y = i / 5 * Math.PI * 2;
      leaf.rotation.z = 0.8;
      g.add(leaf);
      const m = box(0.2, 0.04, 2.4, 0x6BB84A, 0, 0, -1.2, leaf);
      m.scale.y = 0.5;
    }
    // a little bunch
    const bunch = new THREE.Group(); bunch.position.set(0, 2.2, 0); g.add(bunch);
    for (let i = -1; i <= 1; i++){
      cyl(0.08, 0.07, 0.45, PEEL, i*0.15, -0.25, 0, bunch, 8).rotation.z = i*0.2;
    }
  }

  _makeChiquita(){
    const build = (g) => buildBananaGirl(g);
    return new Townsperson({
      name: 'Chiquita', line: 'one sip and you\'ll go bananas.',
      pos: new THREE.Vector3(3, 0, 4.8), facing: Math.PI,
      wander: 0, speed: 0, bubbleY: 2.4, build
    });
  }

  _makeBunch(){
    const build = (g) => buildBananaBunch(g);
    return new Townsperson({
      name: 'The Banana Bunch', line: 'bunch of us, bunch of fun.',
      pos: new THREE.Vector3(-3.5, 0, -1), facing: Math.PI * 0.3,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _talkToChiquita(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.chiquita.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderChiquitaDialog();
  }

  _talkToBunch(){
    this.bread.stop();
    this.bunch.showLine();
  }

  _renderChiquitaDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('chiquita');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${p.have} / ${p.goal} ${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const qbody = questRows.length ? questRows.join('') : '<p class="q-dnone">"Welcome to the village, Bread Boi!"</p>';

    const smoothieCost = 20;
    const afford = getCrumbs() >= smoothieCost;
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Chiquita's Smoothie Stand</h2>
        <div class="cafe-sub">banana village</div>
        <div class="q-drows">${qbody}</div>
        <button class="cafe-btn buy ${afford ? '' : 'disabled'}">buy banana smoothie (🍞 ${smoothieCost})</button>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelector('.buy').onclick = () => {
      if (!spendCrumbs(smoothieCost)) return;
      addCrumbs(0);
      HUD.refreshCrumbs();
      this.bread.drawFace('yum');
      HUD.say('Refreshing banana smoothie! +20 energy');
      restoreEnergy(20);
    };
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this._renderChiquitaDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => { turnIn(b.dataset.id); HUD.refreshCrumbs(); this._renderChiquitaDialog(); });
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _panel(html){
    const el = document.createElement('div');
    el.className = 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _applyWeather(){ this.weather.set(getWeatherState().weather, getWeatherState().season); }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.72];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    // flag the visit quest automatically
    setFlag('visitedBananaVillage', true);
    flagObjective('visit-banana-village', 'visitedBananaVillage');
    const st = getWeatherState();
    HUD.setSubtitle('banana village');
    HUD.say(`Banana Village! ${weatherIcon(st.weather)} ${weatherName(st.weather)} · ${st.temp}°C · ${st.season}.`);
    HUD.setHint('click to walk · talk to Chiquita · head back to town');
    HUD.hideHintSoon(9000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, [this.chiquita, this.bunch], this.bread)) return;

    const hit = this._raycastHotspot(ray);
    if (hit && hit.action){ hit.action(); return; }

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-16, Math.min(16, gp.x));
    gp.z = Math.max(-14, Math.min(14, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _raycastHotspot(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return null;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return null;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    let best = null, bd = Infinity;
    this.hotspots.forEach(h => {
      const d = Math.hypot(p.x - h.pos.x, p.z - h.pos.z);
      if (d < 1.6 && d < bd){ bd = d; best = h; }
    });
    return best;
  }

  update(dt){
    this.bread.update(dt);
    this.chiquita.update(dt, this.bread.root.position);
    this.bunch.update(dt, this.bread.root.position);
    this.ambient.update(dt, 'day');
    this.weather.update(dt, this.bread.root.position);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
